/**
 * De tegenstander: welke zetten mogen, en welke doet hij.
 *
 * `legalActions` somt op wat de regels toestaan. `chooseAction` kiest daaruit, en weegt
 * daarbij met de tabel van zijn eigen karakter — zie `persoonlijkheid.ts`.
 *
 * Het lot loten uit alles wat mag, zoals dit bestand vroeger deed, is niet verdwenen. Het
 * blijft het gedrag voor een speler zonder tabel, en dat is in het origineel geen
 * noodgreep: tegenstander Marlene heeft als enige geen strategiemap, en haar beschrijving
 * zegt dat ze "zonder strategie te werk gaat" (kennisbank 04). Daarnaast blijft het het
 * beste testgereedschap dat er is — duizenden potjes tussen twee lotende spelers vinden
 * vastlopers die gerichte toetsen missen, en `simulation.test.ts` doet precies dat.
 */
import type { Resource, Seat } from '@catan/contracts';
import { BASIC_CARDS, LANDSCAPE_RESOURCE } from '@catan/contracts';
import {
  isLegalAction,
  legalActions,
  nextInt,
  pointsOf,
  type Action,
  type GameState,
  type Rng,
  type Side,
} from '@catan/rules';
import { bedenktAanbod, grondstofwaarde, neemtAanbodAan } from './handel.js';
import { kaartscore, persoonlijkheidVoor, type Profiel } from './persoonlijkheid.js';

/**
 * Wat de computer mag doen — dat zijn de zetten uit de regels, plus zijn eigen aanbod.
 *
 * `legalActions` woont in `packages/rules`, want dat is regelkennis en de server stuurt
 * hem mee. Twee dingen komen er hier bovenop, en die staan er met opzet niet in: welk
 * ruilaanbod hij doet, en of hij er een aanneemt. Dat zijn afwegingen, geen regels.
 */
function zettenVoorDeComputer(state: GameState, seat: Seat): Action[] {
  const wettig = legalActions(state, seat);

  // Ligt er een aanbod, dan geeft `legalActions` allebei de antwoorden. Hij kiest er één.
  if (state.offer !== undefined && state.offer.from !== seat) {
    const wil = neemtAanbodAan(state, seat, { give: state.offer.give, want: state.offer.want });
    const gekozen = wettig.filter(
      (a) => a.type === 'ruil-antwoord' && a.accepteren === wil,
    );
    return gekozen.length > 0 ? gekozen : wettig;
  }

  // Het origineel probeert een aanbod eerst, vóór de bank — ruilen met een mens is altijd
  // gunstiger dan drie-tegen-een.
  if (state.phase === 'hoofdfase' && state.offer === undefined) {
    const aanbod = bedenktAanbod(state, seat);
    if (aanbod !== undefined) {
      const zet: Action = { type: 'ruil-aanbod', give: aanbod.give, want: aanbod.want };
      if (isLegalAction(state, seat, zet).ok) return [...wettig, zet];
    }
  }
  return wettig;
}

/**
 * De vaste waarden voor zetten die geen kaart zijn.
 *
 * Ze staan bewust ruim onder de kaartscores (die lopen van −100 tot ruim boven de 100):
 * bouwen gaat vóór ruilen, ruilen met je tegenstander gaat vóór de bank — dezelfde
 * volgorde die `3a_Entscheidung.ki` aanhoudt — en de beurt afsluiten is het laatste
 * redmiddel. Zonder die laatste regel loopt een potje vast zodra alles even niets waard
 * lijkt.
 */
const VASTE_WAARDE: Readonly<Record<string, number>> = {
  gooi: 1000,
  'ruil-aanbod': 12,
  'ruil-antwoord': 12,
  // Blind de bovenste kaart pakken. Wat eronder ligt weet hij niet — de stapels liggen
  // dicht en je betaalt twee grondstoffen om er één in te zien (handboek p. 15). Hij las
  // hier de bovenste kaart en woog die; dat was vals spel, en het maakte hem sterker dan
  // een mens ooit kan zijn. Een gratis kaart is gemiddeld wat waard, meer weet hij niet.
  'pak-kaart': 20,
  verkenner: 500,
  tegenkaart: 500,
  // Hier ligt de drempel: alles wat hij positief waardeert doet hij liever dan afsluiten,
  // alles daaronder niet. Dat is wat een negatieve basisvoorkeur in het blad betekent —
  // Eleonore staat op −100 bij zeven van de negen ridders, en die bouwt ze dus nooit.
  // Een kaart wegleggen om er daarna een te kunnen pakken. Bewust ónder de drempel: de
  // computer moet dit niet uit zichzelf gaan doen, want dan ruilt hij zijn hand eindeloos
  // rond. Het staat er zodat hij niet vástloopt als het het enige is dat nog mag.
  'ruil-af': -2,
  'beurt-beeindigen': -1,
};

/**
 * Hoeveel ruis er op de score zit, per moeilijkheidsgraad.
 *
 * De sheets zeggen hier niets over — nagezocht in alle 23 bladen — maar `player.cfg` heeft
 * wél een aparte regel `#difficulty`, dus de engine regelt het buiten de tabellen om. Dit
 * is dus een reconstructie, geen meting; zie `kennisbank/05-open-vragen.md`.
 *
 * De getallen zélf zijn wél gemeten, en dat was nodig ook. De eerste indeling (45 / 12 / 0)
 * leverde drie namen op maar twee treden: Meester won maar 50,7% van Gevorderd — statistisch
 * niets. Ruis onder de ~20 verandert de keuze bijna nooit, en boven de ~60 is ze verzadigd
 * omdat hij dan toch al bijna willekeurig grijpt. Deze indeling geeft drie echte treden,
 * gemeten over 200 potjes per paar met hetzelfde karakter aan beide kanten
 * (`tools/speelanalyse.ts`, 29 juli 2026):
 *
 * | | tegen Gevorderd | tegen Beginner |
 * |---|---|---|
 * | **Meester** | 71,5% | 85,0% |
 * | **Gevorderd** | — | 67,0% |
 *
 * Hier stond eerder 62,7 / 79,7 / 65,3. Die meting was niet fout gedaan maar op een
 * kapotte speellus: die gaf de antwoorder van een ruilaanbod nooit het woord, dus ruilen
 * tússen spelers bestond er niet. Met dat gat dicht (`aanZet` in de regels) staan de treden
 * verder uit elkaar — wie scherper speelt heeft méér aan een handelspartner.
 */
const RUIS: Readonly<Record<string, number>> = {
  Beginner: 150,
  Gevorderd: 30,
  Meester: 0,
};

export interface KeuzeOpties {
  /** `Beginner`, `Gevorderd` of `Meester`; iets anders telt als Gevorderd. */
  readonly moeilijkheid?: string;
}

/** Welke kaart-ID hoort bij deze bouwzet? */
function kaartVanZet(actie: Action): number | undefined {
  switch (actie.type) {
    case 'bouw-straat':
      return idVan('Straat');
    case 'bouw-dorp':
      return idVan('Dorp');
    case 'bouw-stad':
      return idVan('Stad');
    case 'bouw-uitbreiding':
    case 'speel-actiekaart':
      return actie.cardId;
    default:
      return undefined;
  }
}

const idVan = (naam: string): number => {
  const kaart = BASIC_CARDS.find((c) => c.name === naam);
  if (kaart === undefined) throw new Error(`kaart ${naam} ontbreekt`);
  return kaart.id;
};

/**
 * Wat een openstaande keuze waard is.
 *
 * Bij een `pending` levert `legalActions` een rij `kies`-zetten waarin de kolom een index
 * draagt — in een stapel, in een hand, of in het vorstendom. Wat die index betekent hangt
 * af van de vraag, dus die lezen we hier zelf uit `state.pending`. Wat er niet bij staat
 * is een keuze tussen landschappen; die zijn onderling zo gelijk dat wegen niets toevoegt.
 */
function scoreVanKeuze(
  state: GameState,
  seat: Seat,
  profiel: Profiel,
  verschil: number,
  actie: Action,
): number {
  const pending = state.pending;
  if (pending === undefined || actie.type !== 'kies') return 0;
  const punt = (id: number | undefined): number =>
    id === undefined ? 0 : kaartscore(profiel, id, verschil);

  switch (pending.kind) {
    // Een kaart uit een stapel die hij mág inzien: dan kiest hij de beste. Hier is dat
    // geen vals spel — voor dit inzagerecht is betaald, of het hoort bij de startkaarten.
    case 'kies-startkaarten':
    case 'kies-uit-stapel':
      return punt(state.stacks[pending.stack]?.[actie.column]);
    case 'spion':
      return punt(state.players[pending.from].hand[actie.column]);
    // Iets kwijtraken: dan gaat juist de kaart weg waar hij het minst om geeft.
    case 'kies-afleggen':
      return -punt(state.players[seat].hand[actie.column]);
    case 'kies-handkaarten':
      return -punt(state.players[pending.from].hand[actie.column]);
    // De Bouwmeester loopt in drie stappen.
    //
    // Bij het kiezen van een **stapel** ligt die nog dicht: wat erin zit weet hij niet, en
    // hij koos hem eerst op de bovenste kaart — dezelfde gluurfout als bij `pak-kaart`,
    // en gevonden door `eerlijk.test.ts`. Wat hij wél mag weten is hoe dik een stapel is,
    // en dat is ook het enige zinnige: meer kaarten is meer keuze.
    //
    // Bij het kiezen van een **kaart** ligt de stapel open — daar is hij net voor naar
    // binnen gestuurd — dus daar mag hij wegen.
    case 'bouwmeester':
      return pending.stap === 'stapel'
        ? (state.stacks[actie.column]?.length ?? 0)
        : pending.stap === 'kaart'
          ? punt(state.stacks[pending.stack ?? -1]?.[actie.column])
          : -punt(state.players[seat].hand[actie.column]);
    default:
      return 0;
  }
}

/** Wat een gewone zet waard is. */
function scoreVanZet(
  state: GameState,
  seat: Seat,
  profiel: Profiel,
  verschil: number,
  actie: Action,
  waarde: Record<Resource, number>,
): number {
  if (state.pending !== undefined) return scoreVanKeuze(state, seat, profiel, verschil, actie);

  if (actie.type === 'ruil-bank') return bankscore(state, seat, actie, waarde);

  const kaart = kaartVanZet(actie);
  if (kaart !== undefined) return kaartscore(profiel, kaart, verschil);
  return VASTE_WAARDE[actie.type] ?? 0;
}

/**
 * Wat een bankruil oplevert — en dat is lang niet altijd iets.
 *
 * Ruilen met de bank **vernietigt** grondstoffen: je geeft er drie weg voor één terug. Als
 * vaste waarde stond hij hier op 6, en daarmee won hij altijd van "beurt afsluiten". Dat
 * had een pijnlijk gevolg dat pas bij het doormeten zichtbaar werd: hoe scherper de
 * computer speelde, hoe vaker hij zat te ruilen in plaats van te bouwen — Meester deed er
 * 15,6% van zijn zetten aan tegen Beginner 12,6% — en dus verloor Meester van Beginner.
 * De ruis van Beginner behoedde hem per ongeluk voor zijn eigen beste zet.
 *
 * Nu rekent hij het gewoon uit, met dezelfde waardering die hij voor een ruilaanbod
 * gebruikt: wat hij terugkrijgt min wat het hem kost. Komt daar niets positiefs uit, dan
 * blijft hij ervan af.
 */
function bankscore(
  state: GameState,
  seat: Seat,
  actie: Action,
  waarde: Record<Resource, number>,
): number {
  if (actie.type !== 'ruil-bank') return 0;
  const speler = state.players[seat];
  const tegel = (plek: { column: number; side: Side }): Resource | undefined => {
    const t = speler.principality.landscapes.find(
      (l) => l.column === plek.column && l.side === plek.side,
    );
    return t === undefined ? undefined : LANDSCAPE_RESOURCE[t.landscape];
  };
  const eerste = actie.give[0];
  const geef = eerste === undefined ? undefined : tegel(eerste);
  const krijg = tegel(actie.receive);
  if (geef === undefined || krijg === undefined) return 0;
  return waarde[krijg] - actie.give.length * waarde[geef];
}

/**
 * Kiest een zet.
 *
 * Vroeger werd hier geloot uit alles wat mocht, met bouwen als voorkeur. Dat is waarom de
 * computer in ronde 1 al een toernooiridder kocht: die zet mág, dus hij kon hem trekken.
 * Nu weegt hij, met de tabel van zijn eigen karakter uit `8_Strategie1.ki`.
 *
 * Twee dingen blijven staan uit de oude opzet. De beurt afsluiten is het laatste
 * redmiddel — anders past hij meteen en gebeurt er nooit iets. En het lot beslist nog
 * steeds mee: hoeveel hangt af van de moeilijkheidsgraad, en bij gelijke score geeft het
 * de doorslag. Dat loopt via de `rng` die we doorgeven, zodat een potje met dezelfde seed
 * hetzelfde verloopt.
 *
 * Een naam zonder tabel — Marlene, of een potje uit een oudere opslag — speelt zoals
 * vroeger: puur op het lot. Bij haar is dat geen terugval maar het origineel.
 */
export function chooseAction(
  state: GameState,
  seat: Seat,
  rng: Rng,
  opties?: KeuzeOpties,
): { rng: Rng; action: Action | undefined } {
  const acties = zettenVoorDeComputer(state, seat);
  if (acties.length === 0) return { rng, action: undefined };

  const profiel = persoonlijkheidVoor(state.players[seat].name);
  if (profiel === undefined) return zonderStrategie(acties, rng);

  const verschil = pointsOf(state, seat) - pointsOf(state, seat === 0 ? 1 : 0);
  const ruis = RUIS[opties?.moeilijkheid ?? ''] ?? RUIS['Gevorderd'] ?? 0;
  // Eén keer uitrekenen wat zijn grondstoffen hem waard zijn: dat hangt aan zijn hand en
  // zijn vorstendom, niet aan de zet die we wegen. Het scheelt gemeten nauwelijks tijd —
  // bankruilen zijn een minderheid van de kandidaten — maar hetzelfde antwoord dertig keer
  // in een lus uitrekenen hoort niet.
  const waarde = grondstofwaarde(state.players[seat]);

  let beste: Action | undefined;
  let hoogste = -Infinity;
  let huidig = rng;
  for (const actie of acties) {
    const basis = scoreVanZet(state, seat, profiel, verschil, actie, waarde);
    // Ruis én een fijne loting bovenop elke score: die tweede breekt gelijke standen, ook
    // op Meester waar de ruis nul is.
    const stap = nextInt(huidig, 1000);
    huidig = stap.rng;
    const score = basis + (stap.value / 1000) * (ruis + 0.001);
    if (score > hoogste) {
      hoogste = score;
      beste = actie;
    }
  }

  return { rng: huidig, action: beste ?? acties[acties.length - 1] };
}

/** De oude manier: loten uit wat mag, met bouwen als voorkeur. Marlene speelt zo. */
function zonderStrategie(acties: Action[], rng: Rng): { rng: Rng; action: Action | undefined } {
  const bouwen = acties.filter((a) => a.type.startsWith('bouw-'));
  // `ruil-af` hoort hier net zo min thuis als `beurt-beeindigen`: het is een keuze om ergens
  // vanaf te komen, niet iets om willekeurig te doen. Zonder deze uitzondering ruilt hij
  // eindeloos rond — hand vol, kaart wegleggen, kaart pakken, hand vol — en komt hij nooit
  // meer aan het einde van zijn beurt, want die staat in de tak die pas aan bod komt als
  // `overig` leeg is.
  const overig = acties.filter(
    (a) => a.type !== 'beurt-beeindigen' && a.type !== 'ruil-af' && !a.type.startsWith('bouw-'),
  );
  const keuzes = bouwen.length > 0 ? bouwen : overig.length > 0 ? overig : acties;

  const step = nextInt(rng, keuzes.length);
  return { rng: step.rng, action: keuzes[step.value] };
}
